import { useState, useRef } from 'react';
import type { AcceptModalConfig } from '../../../types/proposal-content';

interface AcceptModalProps {
  config: AcceptModalConfig;
  isOpen: boolean;
  proposalStatus: string;
  onClose: () => void;
  onSubmit: (data: AcceptSubmitData) => Promise<void>;
}

export interface AcceptSubmitData {
  name: string;
  email: string;
  selectedOptions: Record<string, string>;
  signatureData?: string;
}

export function AcceptModal({ config, isOpen, proposalStatus, onClose, onSubmit }: AcceptModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  if (!isOpen) return null;

  const alreadyAccepted = proposalStatus === 'accepted';

  const handleSubmit = async () => {
    setError('');
    if (!name.trim() || name.trim().length < 2) { setError('Introduce tu nombre completo.'); return; }
    if (!email.trim() || !email.includes('@') || !email.includes('.')) { setError('Introduce un email válido.'); return; }
    if (!accepted) { setError('Debes aceptar las condiciones.'); return; }

    let signatureData: string | undefined;
    if (config.requireSignature && canvasRef.current) {
      signatureData = canvasRef.current.toDataURL();
    }

    setSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), email: email.trim(), selectedOptions, signatureData });
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al enviar. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  // Canvas drawing handlers
  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDrawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      const rect = canvasRef.current!.getBoundingClientRect();
      ctx.beginPath();
      ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) {
      const rect = canvasRef.current!.getBoundingClientRect();
      ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--p-fg1').trim() || '#F0F0F0';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
      ctx.stroke();
    }
  };

  const stopDraw = () => { isDrawingRef.current = false; };

  return (
    <div
      className="fixed inset-0 bg-black/75 z-[200] flex items-center justify-center p-5 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-proposal-bg2 border border-proposal-border-strong rounded-p-lg w-full max-w-[520px] p-9 relative max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-p-sm bg-proposal-bg3 border border-proposal-border text-proposal-fg2 cursor-pointer text-base leading-none hover:text-proposal-fg1"
        >
          ×
        </button>

        {/* Submitted confirmation */}
        {submitted || alreadyAccepted ? (
          <div className="text-center py-5">
            <div className="w-14 h-14 bg-proposal-accent rounded-full flex items-center justify-center mx-auto mb-5 text-[28px]">
              ✓
            </div>
            <h2 className="text-[22px] font-bold text-proposal-fg1 mb-2">{config.confirmationMessage}</h2>
            <p className="text-sm text-proposal-fg2 leading-relaxed">
              {config.confirmationDetail || 'Hemos recibido tu firma. El equipo se pondrá en contacto contigo en las próximas 24 horas.'}
            </p>
            {config.reference && (
              <p className="mt-3 font-proposal-mono text-[12px] text-proposal-fg3">ref: {config.reference}</p>
            )}
          </div>
        ) : (
          <>
            <h2 className="text-[22px] font-bold text-proposal-fg1 mb-1.5">{config.title}</h2>
            <p className="text-[13px] text-proposal-fg2 mb-7">{config.subtitle}</p>

            {/* Option groups (radio) */}
            {config.optionGroups?.map((group, gi) => (
              <div key={gi} className="mb-4.5">
                <label className="block text-[12px] font-semibold text-proposal-fg2 mb-1.5 uppercase tracking-wide">
                  {group.label}
                </label>
                <div className="flex flex-col gap-2">
                  {group.options.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-2.5 p-3 border rounded-p-md cursor-pointer transition-colors hover:border-proposal-border-strong hover:bg-proposal-bg3 ${
                        opt.recommended ? 'border-proposal-accent' : 'border-proposal-border'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`group-${gi}`}
                        value={opt.value}
                        checked={selectedOptions[group.label] === opt.value}
                        onChange={() => setSelectedOptions((s) => ({ ...s, [group.label]: opt.value }))}
                        className="accent-[var(--p-accent)] mt-0.5 shrink-0"
                      />
                      <div className="flex-1">
                        <div className="text-[13px] font-semibold text-proposal-fg1">
                          {opt.label}
                          {opt.recommended && (
                            <span className="ml-1.5 inline-flex items-center bg-proposal-accent text-proposal-accent-text font-proposal-mono text-[9px] font-semibold px-1.5 py-px rounded-full align-middle">
                              RECOMENDADO
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-proposal-fg2 mt-0.5">{opt.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {/* Name */}
            <div className="mb-4.5">
              <label className="block text-[12px] font-semibold text-proposal-fg2 mb-1.5 uppercase tracking-wide">
                Nombre del firmante
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre completo"
                className="w-full bg-proposal-bg border border-proposal-border-strong rounded-p-md px-3.5 py-2.5 text-sm text-proposal-fg1 font-proposal-ui outline-none transition-colors focus:border-proposal-accent"
              />
            </div>

            {/* Email */}
            <div className="mb-4.5">
              <label className="block text-[12px] font-semibold text-proposal-fg2 mb-1.5 uppercase tracking-wide">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                className="w-full bg-proposal-bg border border-proposal-border-strong rounded-p-md px-3.5 py-2.5 text-sm text-proposal-fg1 font-proposal-ui outline-none transition-colors focus:border-proposal-accent"
              />
            </div>

            {/* Signature canvas */}
            {config.requireSignature && (
              <div className="mb-4.5">
                <label className="block text-[12px] font-semibold text-proposal-fg2 mb-1.5 uppercase tracking-wide">
                  Firma
                </label>
                <canvas
                  ref={canvasRef}
                  width={440}
                  height={120}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={stopDraw}
                  onMouseLeave={stopDraw}
                  className="w-full h-[120px] bg-proposal-bg border border-proposal-border-strong rounded-p-md cursor-crosshair"
                />
              </div>
            )}

            {/* Terms checkbox */}
            <div className="mb-4.5">
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="accent-[var(--p-accent)] mt-0.5 shrink-0"
                />
                <span className="text-[13px] text-proposal-fg2">{config.termsText}</span>
              </label>
            </div>

            {/* Error */}
            {error && (
              <p className="text-sm text-red-400 mb-3">{error}</p>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-proposal-accent text-proposal-accent-text border-none rounded-p-md py-3 text-sm font-bold cursor-pointer mt-6 font-proposal-ui transition-opacity hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Enviando...' : 'Firmar y enviar →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
